// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDashboardIsHomepage from "@insite/client-framework/Store/Context/Handlers/SetDashboardIsHomepage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    dashboardIsHomepage: state.context.session.dashboardIsHomepage ?? undefined,
});

const mapDispatchToProps = {
    setDashboardIsHomepage,
};

export interface MyAccountHomepageSelectorStyles {
    checkboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    checkbox?: CheckboxPresentationProps;
}

export const myAccountHomepageSelectorStyles: MyAccountHomepageSelectorStyles = {
    checkboxGroup: {
        css: css`
            align-items: flex-end;
            height: 100%;
            justify-content: center;
        `,
    },
};

interface OwnProps extends WidgetProps {
    extendedStyles?: MyAccountHomepageSelectorStyles;
}

// const styles = myAccountHomepageSelectorStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const MyAccountHomepageSelector = ({ dashboardIsHomepage, setDashboardIsHomepage, extendedStyles }: Props) => {
    const [isCheckboxSelected, setIsCheckboxSelected] = useState(dashboardIsHomepage);
    // XNG-Changes
    const [styles] = React.useState(() => mergeToNew(myAccountHomepageSelectorStyles, extendedStyles));

    const dashboardIsHomepageChangeHandler = (event: React.SyntheticEvent<Element, Event>, value: boolean) => {
        setIsCheckboxSelected(value);
        setDashboardIsHomepage({ dashboardIsHomepage: value });
    };

    return (
        <CheckboxGroup {...styles.checkboxGroup}>
            <Checkbox
                {...styles.checkbox}
                onChange={dashboardIsHomepageChangeHandler}
                checked={isCheckboxSelected}
                data-test-selector="makeMyHomePageCheckbox"
            >
                {siteMessage("Dashboard_MakeMyHomepage")}
            </Checkbox>
        </CheckboxGroup>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyAccountHomepageSelector),
    definition: {
        allowedContexts: ["MyAccountPage"],
        group: "My Account",
        displayName: "Homepage Selector",
    },
};

export default widgetModule;
